import { axiosInstance } from "@/lib/axios";
import type { LoginType } from "@/features/auth/api";

export type UserProfile = {
  id: number;
  app_id: number;
  name: string;
  email: string;
  is_admin: number;
  is_active: number;
  country_code: string;
  mobile_number: string;
  username: string;
  /** The username changes once: false / 0 / "0" / null = still open; true / 1 / "1" = locked (features/settings/username). */
  username_changed?: boolean | number | string | null;
  referral_code: string;
  referral_percent: string;
  blue_check: number;
  lang: string;
  /** Push-notification opt-in, defaults to true at signup. Toggled via POST /send-notify. */
  send_notify?: boolean;
  /** 1 when the account uses Google sign-in. Toggled via POST /google_login. */
  google_login?: number | boolean;
  created_at: string;
  updated_at: string;
  profile: {
    bio: string | null;
    detail: string | null;
    links: string | null;
    cover: string | null;
    banner: string | null;
  };
  wallets: Array<{
    user_id: number;
    network_type: string;
    address: string;
    latest_balance: string;
    network: {
      id: number;
      name: string;
      fa_name: string;
      chain_id: number;
      image: string;
    };
  }>;
  following: {
    following: number;
    follower: number;
  };
};

export type ProfileResponse = {
  data: {
    user: UserProfile;
  };
  message: string | null;
  next: unknown;
  errors: unknown;
  meta: unknown[];
};

export async function getProfile(): Promise<ProfileResponse> {
  const { data } = await axiosInstance.get<ProfileResponse>("/auth/profile");
  return data;
}

export type UpdateProfilePayload = {
  name: string;
  lang: "fa" | "en";
  bio?: string | null;
  detail?: string | null;
  cover?: string | null;
  banner?: string | null;
  links?: Array<object> | null;
  title?: string | null;
  url?: string | null;
  /** Only ever sent to fill a previously-empty email — never a replacement for an existing one. */
  email?: string;
  /** Sent together, only to fill previously-empty mobile contact fields. */
  mobile_number?: string;
  country_code?: string;
  /** The new handle, flat — only while `username_changed` is open and the value differs (features/settings/username). */
  username?: string;
};

export type UpdateProfileResponse = {
  data: {
    user?: UserProfile;
    message?: string;
    /** Set when a just-added missing email/mobile still needs OTP confirmation before it's live. */
    needs_verification?: boolean;
    /** Which contact the pending verification is for. */
    type?: LoginType;
  };
  message: string | null;
  /** "verify_otp" when the added contact needs confirmation via the existing OTP flow. */
  next?: string | null;
  errors: unknown;
  meta: unknown[];
};

export async function updateProfile(payload: UpdateProfilePayload): Promise<UpdateProfileResponse> {
  const { data } = await axiosInstance.post<UpdateProfileResponse>("/auth/profile", payload);
  return data;
}

export type UploadProfileImageResponse = {
  data: { url: string };
  message: string | null;
};

/** Toggles the Google-login flag on the account (google_login: 1|0). */
export async function toggleGoogleLogin(enabled: boolean): Promise<any> {
  const { data } = await axiosInstance.post("/google_login", { google_login: enabled });
  return data;
}

export async function uploadProfileImage(key: "cover" | "banner", file: File): Promise<string> {
  const formData = new FormData();
  formData.append("key", key);
  formData.append("file", file);

  const { data } = await axiosInstance.post<UploadProfileImageResponse>("/auth/upload-profile", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    suppressToast: true,
  } as any);
  return data.data.url;
}
